import { z } from "zod";

export const addressSchema = z.object({
  companyName: z.string().optional().default(""),
  first_name: z.string().min(1, "Le prénom est requis"),
  last_name: z.string().min(1, "Le nom est requis"),
  address1: z.string().min(1, "L'adresse est requise").max(230),
  address2: z.string().max(230).optional().default(""),
  city: z.string().min(1, "La ville est requise"),
  zip: z
    .string()
    .min(1, "Le code postal est requis")
    .transform((v) => v.trim()),
  // Alpha-2 only: carrier compatibility matches country codes by strict string
  // equality against ISO 3166-1 alpha-2 lists, so a country name or an alpha-3
  // code resolves no carrier at all and the return ends up without a label.
  country_code: z
    .string()
    .min(1, "Le pays est requis")
    .regex(/^[A-Za-z]{2}$/, "Le pays doit être un code ISO à 2 lettres"),
  email: z.string().email("E-mail invalide"),
  phone: z.string().min(1, "Le numéro de téléphone est requis"),
});

export type AddressFormValues = z.infer<typeof addressSchema>;

type ProvinceSource = {
  country_code?: string | null;
  province?: string | null;
  province_code?: string | null;
};

/**
 * The form has no state field, so the schema strips `province`/`province_code`
 * from what it parses. Without this, every return was saved without the state
 * Shopify had on the order — a US, CA or AU address that carriers using our
 * address cannot ship from.
 *
 * The state is only carried over while the customer keeps the order's country:
 * one picked for another country would be wrong rather than missing.
 */
export function withOrderProvince<T extends { country_code: string }>(
  values: T,
  orderAddress: ProvinceSource | null | undefined,
): T & { province: string | null; province_code: string | null } {
  const sameCountry =
    !!orderAddress?.country_code &&
    orderAddress.country_code.toUpperCase() ===
      values.country_code.toUpperCase();

  return {
    ...values,
    province: sameCountry ? (orderAddress.province ?? null) : null,
    province_code: sameCountry ? (orderAddress.province_code ?? null) : null,
  };
}
